import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { evaluationQueryService } from "../services/evaluationQueryService";
import {
  EvaluationQueryValuesInit,
  getEvaluationQueryUpdateVariables,
  getEvaluationQueryValues,
  EvaluationQueryValuesType,
  EvaluationQueryErrorsType,
  evaluationQueryDisplayId,
} from "./EvaluationQueryCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { EvaluationQuery } from "../../../api/graphql";
import { isNil } from "../../../common/utils";
import { EvaluationPathType } from "../../../api/graphql";
import { EvaluationDifficulty } from "../../../api/graphql";
import { EvaluationAmbiguityType } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class EvaluationQueryUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: EvaluationQueryErrorsType = {};
  service = evaluationQueryService;
  evaluationQueryInstance: EvaluationQuery | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = EvaluationQueryValuesInit();
  queryError: string | undefined;
  pathTypeError: string | undefined;
  difficultyError: string | undefined;
  expectedToolError: string | undefined;
  expectedArgsError: string | undefined;
  acceptableToolsError: string | undefined;
  expectedResponsePatternError: string | undefined;
  generatedFromError: string | undefined;
  isAmbiguousError: string | undefined;
  ambiguityTypeError: string | undefined;
  disambiguationResponseError: string | undefined;
  notesError: string | undefined;

  private customValidators: CustomValidator<EvaluationQueryValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<EvaluationQueryValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<EvaluationQueryValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadEvaluationQueryById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.evaluationQueryInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.evaluationQueryInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getEvaluationQueryValues(
        this.evaluationQueryInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadEvaluationQuery(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() =>
      this.loadEvaluationQueryById(this.entityValues.id)
    );
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return evaluationQueryDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.evaluationQueryInstance) {
      return evaluationQueryDisplayId(
        this.evaluationQueryInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EvaluationQuerySingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupEvaluationQueryLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  returnedData(_data: any): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getQueryValue(): string {
    return this.entityValues.query;
  }

  setQueryValue(value: string) {
    this.entityValues.query = value;
  }

  getPathTypeValue(): EvaluationPathType {
    return this.entityValues.pathType;
  }

  setPathTypeValue(value: EvaluationPathType) {
    this.entityValues.pathType = value;
  }

  getPathTypeOptions() {
    return this.langStore.EvaluationPathTypeOptions;
  }

  getDifficultyValue(): EvaluationDifficulty {
    return this.entityValues.difficulty;
  }

  setDifficultyValue(value: EvaluationDifficulty) {
    this.entityValues.difficulty = value;
  }

  getDifficultyOptions() {
    return this.langStore.EvaluationDifficultyOptions;
  }

  getExpectedToolValue(): string {
    return this.entityValues.expectedTool;
  }

  setExpectedToolValue(value: string) {
    this.entityValues.expectedTool = value;
  }

  getExpectedArgsValue(): string {
    return this.entityValues.expectedArgs;
  }

  setExpectedArgsValue(value: string) {
    this.entityValues.expectedArgs = value;
  }

  getAcceptableToolsValue(): string {
    return this.entityValues.acceptableTools;
  }

  setAcceptableToolsValue(value: string) {
    this.entityValues.acceptableTools = value;
  }

  getExpectedResponsePatternValue(): string {
    return this.entityValues.expectedResponsePattern;
  }

  setExpectedResponsePatternValue(value: string) {
    this.entityValues.expectedResponsePattern = value;
  }

  getGeneratedFromValue(): string {
    return this.entityValues.generatedFrom;
  }

  setGeneratedFromValue(value: string) {
    this.entityValues.generatedFrom = value;
  }

  getIsAmbiguousValue(): boolean {
    return this.entityValues.isAmbiguous;
  }

  setIsAmbiguousValue(value: boolean) {
    this.entityValues.isAmbiguous = value;
  }

  getAmbiguityTypeValue(): EvaluationAmbiguityType | "" {
    return this.entityValues.ambiguityType;
  }

  setAmbiguityTypeValue(value: EvaluationAmbiguityType | "") {
    this.entityValues.ambiguityType = value;
  }

  getAmbiguityTypeOptions() {
    return this.langStore.EvaluationAmbiguityTypeOptions;
  }

  getDisambiguationResponseValue(): string {
    return this.entityValues.disambiguationResponse;
  }

  setDisambiguationResponseValue(value: string) {
    this.entityValues.disambiguationResponse = value;
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.evaluationQueryInstance = null;
    this.entityValues = EvaluationQueryValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: EvaluationQueryValuesType): EvaluationQueryErrorsType {
    const errors: EvaluationQueryErrorsType = {};
    this.clearErrors();
    values.query = values.query.trim();
    if (values.query === "") {
      errors.query = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EvaluationQueryQueryLabel,
      };
    }

    if (!values.pathType) {
      errors.pathType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EvaluationQueryPathTypeLabel,
      };
    }

    if (!values.difficulty) {
      errors.difficulty = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EvaluationQueryDifficultyLabel,
      };
    }
    values.expectedTool = values.expectedTool.trim();
    if (values.expectedTool === "") {
      errors.expectedTool = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EvaluationQueryExpectedToolLabel,
      };
    }

    if (isNil(values.isAmbiguous)) {
      errors.isAmbiguous = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EvaluationQueryIsAmbiguousLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "EvaluationQueryUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      query: this.entityValues.query,
      pathType: this.entityValues.pathType,
      difficulty: this.entityValues.difficulty,
      expectedTool: this.entityValues.expectedTool,
      expectedArgs: this.entityValues.expectedArgs,
      acceptableTools: this.entityValues.acceptableTools,
      expectedResponsePattern: this.entityValues.expectedResponsePattern,
      generatedFrom: this.entityValues.generatedFrom,
      isAmbiguous: this.entityValues.isAmbiguous,
      ambiguityType: this.entityValues.ambiguityType,
      disambiguationResponse: this.entityValues.disambiguationResponse,
      notes: this.entityValues.notes,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveEvaluationQuery(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadEvaluationQueryById(this.savedId);
              } catch (error) {
                console.warn(
                  "[EvaluationQueryUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelEvaluationQuery(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getEvaluationQueryValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = EvaluationQueryValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.evaluationQueryInstance) {
      const variables = getEvaluationQueryUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.evaluationQueryUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.evaluationQueryUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.evaluationQueryUpdate.version_;
      this.saveAttempt = null;
      this.evaluationQueryInstance =
        result.evaluationQueryUpdate as EvaluationQuery;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadEvaluationQueryById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
